/**
 * EngineeringDashboard — workload técnico (Engenharia).
 *
 * Quando o utilizador é `engineering_responsible` em pelo menos uma RFQ, o
 * backend filtra por ele. Caso contrário (chefes/coordenadores sem
 * atribuições próprias), mostra a vista global da Engenharia.
 *
 * Layout:
 *   ▸ 5 KPIs: RFQs em análise, SLA-overdue, tempo médio em Eng,
 *     entregues no período (throughput), carga próxima 7 dias.
 *     [O antigo "Retrabalho" foi removido — versionar não é sinónimo
 *     de retrabalho da Eng, podem ser modificações comerciais legítimas.]
 *   ▸ 2 charts: fluxo entradas vs saídas da Engenharia (semanal),
 *     distribuição da minha responsabilidade por estado.
 *   ▸ 2 charts adicionais: máquinas e operações de acabamento que
 *     historicamente aparecem em RFQs ganhas — útil para dimensionar
 *     a capacidade técnica que a Eng terá de assegurar.
 *   ▸ Tabela "Acções pendentes" — RFQs `IN_ANALYSIS` que estão à espera
 *     de mim (atribuídas ao meu user, ou todas se sou chefe sem
 *     atribuições próprias), ordenadas por urgência (deadline ASC).
 */
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import {
  KpiGrid, DonutChartCard, TimeSeriesCard, OperationalTable,
  HorizontalBarCard,
} from '@/components/dashboard';
import { formatDate } from '@/lib/utils';
import { t } from '@/i18n/translations';

export default function EngineeringDashboard({ data, lang }) {
  const charts = data.charts || {};
  const tables = data.tables || {};

  return (
    <div className="space-y-6">
      <KpiGrid kpis={data.kpis || []} lang={lang} />

      <div className="grid gap-4 lg:grid-cols-2">
        <TimeSeriesCard
          title={t('chart_engineering_flow', lang)}
          data={charts.engineering_flow || []}
          series={[
            { key: 'entered', labelKey: 'series_entered', color: '#3B82F6' },
            { key: 'left',    labelKey: 'series_left',    color: '#10B981' },
          ]}
          kind="bar"
          lang={lang}
        />
        <DonutChartCard
          title={t('chart_eng_distribution', lang)}
          data={charts.own_status_eng || []}
          lang={lang}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <HorizontalBarCard
          title={t('chart_machines_won_eng', lang)}
          data={charts.machines_won || []}
          lang={lang}
          emptyKey="no_won_data"
        />
        <HorizontalBarCard
          title={t('chart_finishing_won_eng', lang)}
          data={charts.finishing_won || []}
          lang={lang}
          showPercent
          emptyKey="no_won_data"
        />
      </div>

      <OperationalTable
        title={t('table_pending_action_eng', lang)}
        items={tables.queue || []}
        lang={lang}
        emptyKey="no_pending_action"
        columns={[
          { key: 'reference_number', labelKey: 'rfq_ref',
            render: (r) => (
              <Link to={`/rfqs/${r.id}`} className="font-medium hover:underline">
                {r.reference_number}
              </Link>
            ),
          },
          { key: 'part_designation', labelKey: 'part_designation',
            render: (r) => (
              <span className="text-muted-foreground">{r.part_designation}</span>
            ),
          },
          { key: 'deadline', labelKey: 'deadline',
            render: (r) => (
              <span className={r.is_overdue ? 'tnum text-destructive' : 'tnum'}>
                {formatDate(r.deadline, lang)}
              </span>
            ),
          },
          { key: 'days_to_deadline', labelKey: 'days_remaining', align: 'right',
            render: (r) => {
              if (r.days_to_deadline == null) return <span className="text-muted-foreground">—</span>;
              return (
                <Badge variant={r.is_overdue ? 'destructive' : (r.days_to_deadline <= 3 ? 'warning' : 'muted')}>
                  {r.is_overdue
                    ? `${Math.abs(r.days_to_deadline)} ${t('days_overdue_suffix', lang)}`
                    : `${r.days_to_deadline} d`
                  }
                </Badge>
              );
            },
          },
        ]}
      />
    </div>
  );
}
